import React, { memo, useState } from "react";
import { Box, InputAdornment, Popover, TextField, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useController } from "react-hook-form";
import dayjs from "dayjs";
import "dayjs/locale/en-in";

const TransactionDateField = memo(function TransactionDateField({ control }) {
  const { field, fieldState } = useController({ name: "dateOfTransaction", control });
  const [anchor, setAnchor] = useState(null);
  const selected = field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null;
  const open = Boolean(anchor);
  const closeCalendar = () => {
    setAnchor(null);
    field.onBlur();
  };

  return (
    <Box sx={{ mt: 2.5 }}>
      <TextField
        label="Date of Transaction"
        name={field.name}
        value={selected ? selected.format("DD MMM YYYY") : ""}
        placeholder="Select a date"
        inputRef={field.ref}
        onClick={(event) => setAnchor(event.currentTarget)}
        onBlur={field.onBlur}
        onKeyDown={(event) => {
          if (["Enter", " ", "ArrowDown"].includes(event.key)) {
            event.preventDefault();
            setAnchor(event.currentTarget);
          }
        }}
        required
        error={!!fieldState.error}
        helperText={fieldState.error?.message}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            readOnly: true,
            endAdornment: <InputAdornment position="end"><CalendarMonthOutlinedIcon sx={{ fontSize: 21, color: "var(--primary-color, #1976d2)" }} /></InputAdornment>,
          },
          htmlInput: {
            role: "combobox",
            "aria-label": "Date of Transaction",
            "aria-expanded": open,
            "aria-haspopup": "dialog",
            "aria-controls": open ? "transaction-date-calendar" : undefined,
          },
        }}
        sx={{
          width: { xs: "100%", sm: 270 },
          cursor: "pointer",
          "& .MuiOutlinedInput-root": {
            height: 50,
            borderRadius: "10px",
            backgroundColor: "#fff",
            cursor: "pointer",
            "& fieldset": { borderColor: "#cbd5e1" },
            "&:hover fieldset": { borderColor: "var(--primary-color, #1976d2)" },
            "&.Mui-focused fieldset": { borderColor: "var(--primary-color, #1976d2)" },
          },
          "& input": { fontSize: 14, fontWeight: 500, color: "#334155", cursor: "pointer" },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={closeCalendar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 1, borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 16px 48px rgba(15, 23, 42, 0.16)", overflow: "hidden" } } }}
      >
        {open && (
          <Box id="transaction-date-calendar" role="dialog" aria-label="Choose transaction date">
            <Box sx={{ px: 2.5, py: 2, background: "var(--primary-gradient, var(--primary-color, #1976d2))", color: "var(--primary-contrast, #fff)" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.7, textTransform: "uppercase", opacity: 0.85 }}>Transaction date</Typography>
              <Typography sx={{ mt: 0.5, fontSize: 23, fontWeight: 700 }}>{selected ? selected.format("ddd, DD MMM YYYY") : "Choose a date"}</Typography>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-in">
              <DateCalendar
                value={selected}
                maxDate={dayjs()}
                views={["year", "month", "day"]}
                reduceAnimations
                onChange={(date, selectionState) => {
                  if (date?.isValid()) field.onChange(date.startOf("day").toDate());
                  if (selectionState === "finish") closeCalendar();
                }}
                sx={{
                  "& .MuiPickersCalendarHeader-label": { fontSize: 14, fontWeight: 700, color: "#0f172a" },
                  "& .MuiPickersCalendarHeader-root": { mt: 1.5, mb: 1 },
                  "& .MuiDayCalendar-weekDayLabel": { fontSize: 11, fontWeight: 700, color: "#94a3b8" },
                  "& .MuiPickersDay-root": { borderRadius: "10px", fontSize: 13, fontWeight: 500 },
                  "& .MuiPickersDay-root:hover": { backgroundColor: "var(--primary-light, #eff6ff)" },
                  "& .MuiPickersDay-root.Mui-selected, & .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus": { backgroundColor: "var(--primary-color, #1976d2)", color: "var(--primary-contrast, #fff)", fontWeight: 700 },
                  "& .MuiPickersDay-today:not(.Mui-selected)": { borderColor: "var(--primary-color, #1976d2)", color: "var(--primary-color, #1976d2)" },
                  "& .MuiYearCalendar-button.Mui-selected, & .MuiMonthCalendar-button.Mui-selected": { backgroundColor: "var(--primary-color, #1976d2)", color: "var(--primary-contrast, #fff)" },
                }}
              />
            </LocalizationProvider>
          </Box>
        )}
      </Popover>
    </Box>
  );
});

export default TransactionDateField;
